import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatMoney, type MoneyLanguage } from '../../shared/format/money';
import { formatTimestamp } from '../../shared/format/time';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { toPageError } from '../partners/PartnerListPage';
import { ReportDrilldowns } from '../reports/ReportDrilldowns';
import { AssignmentChangeDialog } from './AssignmentChangeDialog';
import { AssignmentHistoryPanel } from './AssignmentHistoryPanel';
import { DirectDownlinesPanel } from './DirectDownlinesPanel';
import { MemberStatusChip } from './MemberListPage';
import { MemberStatusDialog } from './MemberStatusDialog';
import { ReferralCodeDialog } from './ReferralCodeDialog';
import { memberRepositoryToken } from './repository';
import type { AssignmentChangeSet, BindPartnerUserInput, MemberDetail } from './types';

export function MemberDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { memberId = '' } = useParams();
  const repository = useRepository(memberRepositoryToken);
  const auth = useAuth();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState<'overview' | 'downlines' | 'assignments'>('overview');
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [bindingOpen, setBindingOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.get(memberId, signal),
    [memberId, refreshKey, repository],
    () => false,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('MEMBER_MANAGE');
  const refresh = () => setRefreshKey((key) => key + 1);
  const saved = (_member: MemberDetail) => refresh();
  const assignmentSaved = (changeSet: AssignmentChangeSet) => {
    setAssignmentOpen(false);
    void navigate(`/member-assignment-change-sets/${changeSet.id}`);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('members.errors.detail'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              actions={
                canManage ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    {resource.data.memberType === 'HOUSE' &&
                    resource.data.partnerUserId === null ? (
                      <Button onClick={() => setBindingOpen(true)} variant="outlined">
                        {t('members.actions.bindPartnerUser')}
                      </Button>
                    ) : null}
                    <Button
                      disabled={resource.data.status === 'CLOSED'}
                      onClick={() => setAssignmentOpen(true)}
                      variant="outlined"
                    >
                      {t('members.actions.changeAssignment')}
                    </Button>
                    <Button
                      disabled={
                        resource.data.status === 'CLOSED' || resource.data.memberType === 'HOUSE'
                      }
                      onClick={() => setReferralOpen(true)}
                      variant="outlined"
                    >
                      {t('members.actions.rotateReferral')}
                    </Button>
                    <Button
                      color="warning"
                      disabled={resource.data.status === 'CLOSED'}
                      onClick={() => setStatusOpen(true)}
                      variant="contained"
                    >
                      {t('members.actions.changeStatus')}
                    </Button>
                  </Stack>
                ) : undefined
              }
              backTo={paths.members}
              dataSource={dataSource}
              subtitle={resource.data.partnerUserId ?? resource.data.id}
              title={resource.data.username ?? resource.data.id}
            />
            <Tabs
              aria-label={t('members.detail.tabsLabel')}
              onChange={(_event, value) => setTab(value as typeof tab)}
              value={tab}
              variant="scrollable"
            >
              <Tab label={t('members.detail.overview')} value="overview" />
              <Tab label={t('members.detail.downlines')} value="downlines" />
              <Tab label={t('members.detail.assignments')} value="assignments" />
            </Tabs>
            {tab === 'overview' ? (
              <MemberOverview language={language} member={resource.data} />
            ) : null}
            {tab === 'downlines' ? <DirectDownlinesPanel memberId={resource.data.id} /> : null}
            {tab === 'assignments' ? <AssignmentHistoryPanel memberId={resource.data.id} /> : null}
            <RelatedRecords member={resource.data} />
            <ReportDrilldowns
              metrics={[
                {
                  kind: 'transactions',
                  filters: { memberId: resource.data.id },
                  label: t('reportSummaries.metrics.transactions'),
                  value: null,
                },
                {
                  kind: 'user-affiliate-by-game',
                  filters: { memberId: resource.data.id },
                  label: t('reportSummaries.metrics.affiliateByGame'),
                  value: null,
                },
                {
                  kind: 'wallets',
                  filters: { memberId: resource.data.id },
                  label: t('reportSummaries.metrics.wallet'),
                  value:
                    resource.data.defaultWallet === null
                      ? null
                      : `${t('members.fields.available')}: ${formatMoney(
                          resource.data.defaultWallet.availableBalance,
                          resource.data.defaultWallet.currencyCode,
                          language,
                        )} · ${t('members.fields.reserved')}: ${formatMoney(
                          resource.data.defaultWallet.reservedBalance,
                          resource.data.defaultWallet.currencyCode,
                          language,
                        )}`,
                },
                {
                  kind: 'withdrawals',
                  filters: { memberId: resource.data.id },
                  label: t('reportSummaries.metrics.withdrawals'),
                  value: null,
                },
                {
                  kind: 'affiliate-commissions',
                  filters: { memberId: resource.data.id },
                  label: t('reportSummaries.metrics.generatedCommission'),
                  value: null,
                },
              ]}
            />
          </Stack>
        )}
      </PageState>
      {resource.data === null ? null : (
        <>
          <AssignmentChangeDialog
            dataSource={dataSource}
            member={resource.data}
            onClose={() => setAssignmentOpen(false)}
            onSaved={assignmentSaved}
            open={assignmentOpen}
          />
          <PartnerUserBindingDialog
            member={resource.data}
            onClose={() => setBindingOpen(false)}
            onSaved={saved}
            open={bindingOpen}
          />
          <ReferralCodeDialog
            member={resource.data}
            onClose={() => setReferralOpen(false)}
            onSaved={saved}
            open={referralOpen}
          />
          <MemberStatusDialog
            member={resource.data}
            onClose={() => setStatusOpen(false)}
            onSaved={saved}
            open={statusOpen}
          />
        </>
      )}
    </Box>
  );
}

function PartnerUserBindingDialog({
  member,
  onClose,
  onSaved,
  open,
}: {
  readonly member: MemberDetail;
  readonly onClose: () => void;
  readonly onSaved: (member: MemberDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(memberRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<Pick<BindPartnerUserInput, 'partnerUserId' | 'reason'>>({
    defaultValues: { partnerUserId: '', reason: '' },
  });
  const close = () => {
    if (guard.current) return;
    reset();
    setError(null);
    onClose();
  };
  const submit = handleSubmit(async (values) => {
    if (guard.current || member.username === null) return;
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.bindPartnerUser(member.id, {
        partnerUserId: values.partnerUserId.trim(),
        expectedUsername: member.username,
        reason: values.reason.trim(),
      });
      reset();
      onClose();
      onSaved(result.data);
    } catch (submitError) {
      setError(toDialogError(submitError, t('members.errors.binding')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('members.binding.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="partner-user-binding-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('members.binding.impact', { username: member.username ?? member.id })}
          </Alert>
          <TextField
            error={errors.partnerUserId !== undefined}
            helperText={errors.partnerUserId?.message}
            label={t('members.fields.partnerUserId')}
            required
            {...register('partnerUserId', {
              required: t('members.validation.partnerUserRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.partnerUserRequired'),
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('members.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('members.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button form="partner-user-binding-form" loading={busy} type="submit" variant="contained">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberOverview({
  language,
  member,
}: {
  readonly language: MoneyLanguage;
  readonly member: MemberDetail;
}) {
  const { t } = useTranslation();
  const registered = formatTimestamp(member.registeredAt, 'Asia/Bangkok', language);
  const created = formatTimestamp(member.createdAt, 'Asia/Bangkok', language);
  const updated = formatTimestamp(member.updatedAt, 'Asia/Bangkok', language);
  const assignment = member.currentAssignment;
  const wallet = member.defaultWallet;
  return (
    <Paper component="section" variant="outlined" sx={{ p: 2 }}>
      <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
        {t('members.detail.overview')}
      </Typography>
      <MetadataList
        items={[
          {
            label: t('members.fields.partner'),
            value: (
              <MuiLink component={Link} to={`/partners/${member.partnerId}`}>
                {member.partnerId}
              </MuiLink>
            ),
          },
          {
            label: t('members.fields.partnerUserId'),
            value: member.partnerUserId ?? t('common.notAvailable'),
          },
          { label: t('members.fields.type'), value: member.memberType },
          { label: t('members.fields.status'), value: <MemberStatusChip status={member.status} /> },
          {
            label: t('members.fields.referralCode'),
            value: member.activeReferralCodes.join(', ') || t('common.notAvailable'),
          },
          {
            label: t('members.fields.agent'),
            value:
              assignment === null ? (
                t('common.notAvailable')
              ) : (
                <MuiLink component={Link} to={`/agents/${assignment.agent.id}`}>
                  {assignment.agent.code} · {assignment.agent.name}
                </MuiLink>
              ),
          },
          {
            label: t('members.fields.directUpline'),
            value:
              assignment?.upline === null || assignment?.upline === undefined ? (
                t('common.notAvailable')
              ) : (
                <MuiLink component={Link} to={`/members/${assignment.upline.memberId}`}>
                  {assignment.upline.username ?? assignment.upline.memberId}
                </MuiLink>
              ),
          },
          {
            label: t('members.fields.assignmentType'),
            value: assignment?.assignmentType ?? t('common.notAvailable'),
          },
          { label: t('members.fields.downlines'), value: member.directDownlineCount },
          {
            label: t('members.fields.agentDefaultUpline'),
            value: member.isAgentDefaultUpline ? t('common.yes') : t('common.no'),
          },
          {
            label: t('members.fields.walletStatus'),
            value: wallet?.status ?? t('common.notAvailable'),
          },
          {
            label: t('members.fields.available'),
            value:
              wallet === null
                ? t('common.notAvailable')
                : formatMoney(wallet.availableBalance, wallet.currencyCode, language),
          },
          {
            label: t('members.fields.reserved'),
            value:
              wallet === null
                ? t('common.notAvailable')
                : formatMoney(wallet.reservedBalance, wallet.currencyCode, language),
          },
          {
            label: t('members.fields.registeredAt'),
            value: `${registered.display} (${registered.originalOffset})`,
          },
          {
            label: t('members.fields.createdAt'),
            value: `${created.display} (${created.originalOffset})`,
          },
          {
            label: t('members.fields.updatedAt'),
            value: `${updated.display} (${updated.originalOffset})`,
          },
        ]}
      />
    </Paper>
  );
}

function RelatedRecords({ member }: { readonly member: MemberDetail }) {
  const { t } = useTranslation();
  const id = encodeURIComponent(member.id);
  return (
    <Paper
      component="nav"
      aria-label={t('members.detail.related')}
      variant="outlined"
      sx={{ p: 2 }}
    >
      <Typography component="h2" variant="h6">
        {t('members.detail.related')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
        <MuiLink component={Link} to={`${paths.transactions}?memberId=${id}`}>
          {t('members.detail.transactions')}
        </MuiLink>
        <MuiLink component={Link} to={`${paths.affiliateWallets}?memberId=${id}`}>
          {t('members.detail.wallets')}
        </MuiLink>
        <MuiLink component={Link} to={`${paths.affiliateWithdrawals}?memberId=${id}`}>
          {t('members.detail.withdrawals')}
        </MuiLink>
        <MuiLink component={Link} to={`${paths.audit}?entityType=MEMBER&entityId=${id}`}>
          {t('members.detail.audit')}
        </MuiLink>
      </Stack>
    </Paper>
  );
}

export function AssignmentChangeSetPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { changeSetId = '' } = useParams();
  const repository = useRepository(memberRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: MoneyLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.getAssignmentChangeSet(changeSetId, signal),
    [changeSetId, refreshKey, repository],
    () => false,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('MEMBER_MANAGE');
  const refresh = () => setRefreshKey((key) => key + 1);
  return (
    <PageState
      error={toPageError(resource.error, t('members.errors.changeSet'))}
      onRetry={refresh}
      status={resource.status}
    >
      {resource.data === null ? null : (
        <Stack spacing={2}>
          <DetailHeader
            actions={
              canManage ? (
                <Button
                  color="warning"
                  disabled={!isAssignmentChangeSetCancellable(resource.data, Date.now())}
                  onClick={() => setCancelOpen(true)}
                  variant="contained"
                >
                  {t('members.actions.cancelChangeSet')}
                </Button>
              ) : undefined
            }
            backTo={paths.members}
            dataSource={dataSource}
            subtitle={resource.data.reason}
            title={t('members.changeSet.title', { id: resource.data.id })}
          />
          <Paper component="section" variant="outlined" sx={{ p: 2 }}>
            <MetadataList
              items={[
                {
                  label: t('members.changeSet.status'),
                  value: <AssignmentStatusText status={resource.data.status} />,
                },
                { label: t('members.changeSet.mode'), value: resource.data.mode },
                {
                  label: t('members.fields.effectiveAt'),
                  value: timestampWithOffset(resource.data.effectiveAt, language),
                },
                { label: t('members.fields.reason'), value: resource.data.reason },
                { label: t('members.assignment.previewHash'), value: resource.data.previewHash },
                {
                  label: t('members.changeSet.createdAt'),
                  value: timestampWithOffset(resource.data.createdAt, language),
                },
              ]}
            />
          </Paper>
          <Paper component="section" variant="outlined" sx={{ p: 2 }}>
            <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
              {t('members.changeSet.items')}
            </Typography>
            <Stack spacing={1}>
              {resource.data.changes.map((change) => (
                <Alert key={change.id} severity="info">
                  <MuiLink component={Link} to={`/members/${change.memberId}`}>
                    {change.memberId}
                  </MuiLink>
                  {` · ${change.before?.agentId ?? t('common.notAvailable')} → ${change.after.agentId}`}
                </Alert>
              ))}
            </Stack>
          </Paper>
          <CancelChangeSetDialog
            changeSet={resource.data}
            onClose={() => setCancelOpen(false)}
            onSaved={refresh}
            open={cancelOpen}
          />
        </Stack>
      )}
    </PageState>
  );
}

function CancelChangeSetDialog({
  changeSet,
  onClose,
  onSaved,
  open,
}: {
  readonly changeSet: AssignmentChangeSet;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
}) {
  const repository = useRepository(memberRepositoryToken);
  const { t } = useTranslation();
  const guard = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<{ reason: string }>({ defaultValues: { reason: '' } });
  const close = () => {
    if (!guard.current) {
      reset();
      setError(null);
      onClose();
    }
  };
  const submit = handleSubmit(async ({ reason }) => {
    if (guard.current) return;
    if (!isAssignmentChangeSetCancellable(changeSet, Date.now())) {
      setError({ message: t('members.changeSet.elapsed'), requestId: null });
      return;
    }
    guard.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.cancelAssignmentChangeSet(changeSet.id, { reason: reason.trim() });
      onSaved();
      reset();
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('members.errors.cancelChangeSet')));
    } finally {
      guard.current = false;
      setBusy(false);
    }
  });
  return (
    <Dialog fullWidth onClose={close} open={open}>
      <DialogTitle>{t('members.changeSet.cancelTitle')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="cancel-change-set-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <Alert severity="warning">
            {t('members.changeSet.cancelImpact', {
              id: changeSet.id,
              current: changeSet.status,
              next: 'CANCELLED',
            })}
          </Alert>
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('members.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('members.validation.reasonRequired'),
              validate: (value) =>
                value.trim().length > 0 || t('members.validation.reasonRequired'),
            })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          form="cancel-change-set-form"
          loading={busy}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function isAssignmentChangeSetCancellable(
  changeSet: AssignmentChangeSet,
  nowMilliseconds: number,
): boolean {
  const effectiveAt = Date.parse(changeSet.effectiveAt);
  return (
    changeSet.status === 'SCHEDULED' &&
    Number.isFinite(effectiveAt) &&
    effectiveAt > nowMilliseconds
  );
}

function AssignmentStatusText({ status }: { readonly status: AssignmentChangeSet['status'] }) {
  return (
    <Typography
      color={
        status === 'CANCELLED'
          ? 'text.secondary'
          : status === 'SCHEDULED'
            ? 'warning.main'
            : 'success.main'
      }
      component="span"
      sx={{ fontWeight: 700 }}
    >
      {status}
    </Typography>
  );
}

function timestampWithOffset(value: string, language: MoneyLanguage) {
  const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}
